import { validateEmailFn } from "../../Core/Functions/validateEmailFn";
import type {
  BookingFormErrorsModel,
  BookingFormValuesModel,
  BookingRecordModel,
  BookingSubmissionModel,
  PaymentOptionModel,
  ShootTypeModel,
} from "../../Core/Models/BookingModels";

export const createInitialBookingFormValuesFn = (
  packageId = ""
): BookingFormValuesModel => ({
  packageId,
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  preferredDate: "",
  preferredTime: "",
  availabilitySlotId: "",
  shootType: "",
  projectNotes: "",
  paymentOption: "deposit",
});

export const getShootTypeOptionsFn = (): Array<{
  label: string;
  value: ShootTypeModel;
}> => [
  { label: "Personal branding", value: "personal-branding" },
  { label: "Product photography", value: "product-photography" },
  { label: "Lifestyle imagery", value: "lifestyle-product-shoot" },
  { label: "Content for social media", value: "content-for-social" },
  { label: "Team and business", value: "team-and-business" },
];

export const buildRequestedAmountFn = (
  depositAmount: number,
  fullAmount: number,
  paymentOption: PaymentOptionModel
) => (paymentOption === "deposit" ? depositAmount : fullAmount);

export const validateBookingFormFn = (
  values: BookingFormValuesModel
): BookingFormErrorsModel => {
  const errors: BookingFormErrorsModel = {};

  if (!values.packageId) {
    errors.packageId = "Choose a package.";
  }

  if (!values.contactName.trim()) {
    errors.contactName = "Name is required.";
  }

  if (!values.contactEmail.trim()) {
    errors.contactEmail = "Email is required.";
  } else if (!validateEmailFn(values.contactEmail)) {
    errors.contactEmail = "Enter a valid email address.";
  }

  if (!values.contactPhone.trim()) {
    errors.contactPhone = "Phone is required.";
  }

  if (!values.preferredDate) {
    errors.preferredDate = "Choose a date.";
  }

  if (!values.preferredTime) {
    errors.preferredTime = "Choose a time.";
  }

  if (!values.availabilitySlotId) {
    errors.availabilitySlotId = "Select an available slot.";
  }

  if (!values.shootType) {
    errors.shootType = "Select a shoot type.";
  }

  return errors;
};

export const buildBookingRecordFn = ({
  booking,
  selectedPackage,
}: BookingSubmissionModel): BookingRecordModel => {
  const timestamp = new Date().toISOString();

  return {
    id: `booking-${Math.random().toString(36).slice(2, 10)}`,
    packageId: booking.packageId,
    packageName: selectedPackage.name,
    contactName: booking.contactName.trim(),
    contactEmail: booking.contactEmail.trim().toLowerCase(),
    contactPhone: booking.contactPhone.trim(),
    preferredDate: booking.preferredDate,
    preferredTime: booking.preferredTime,
    availabilitySlotId: booking.availabilitySlotId,
    shootType: booking.shootType as ShootTypeModel,
    projectNotes: booking.projectNotes.trim(),
    paymentOption: booking.paymentOption,
    requestedAmount: buildRequestedAmountFn(
      selectedPackage.depositAmount,
      selectedPackage.fullAmount,
      booking.paymentOption
    ),
    status: "requested",
    source: "local",
    createdAt: timestamp,
    updatedAt: timestamp,
  };
};
